import React, { useEffect, useState } from 'react';
import { CzechSuklRsAtcGroupResponse } from 'com.cogvio.pricemonitor.api';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { PaginationPanel } from '~/libs/ui/molecules/PaginationPanel';
import { useAvailabilityData } from '~/pages/Availability/adapters/useAvailabilityData';
import { Filters, FiltersValue } from '~/pages/Availability/components/Filters';
import { Row } from '~/pages/Availability/components/Row';
import { DDD_SHARE_THRESHOLD, DEFAULT_PER_PAGE } from '~/pages/Availability/constants';
import { buildAvailabilityParams } from '~/pages/Availability/utils/availabilityQueryParams';

interface Props {
	onSelectionChange(groups: CzechSuklRsAtcGroupResponse[]): void;
}

function hasActiveFilters(filters: FiltersValue): boolean {
	return filters.items.length > 0 || filters.rs.length > 0;
}

export function AvailabilitySelector({ onSelectionChange }: Props) {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState<FiltersValue>({ items: [], rs: [] });
	const [selectedGroups, setSelectedGroups] = useState<Map<string, CzechSuklRsAtcGroupResponse>>(new Map());

	const { data, isFetching, isError } = useAvailabilityData(buildAvailabilityParams(filters, page, DEFAULT_PER_PAGE));
	const filtersActive = hasActiveFilters(filters);

	const handleFiltersChange = (value: FiltersValue) => {
		setFilters(value);
		setPage(1);
		setSelectedGroups(new Map());
		onSelectionChange([]);
	};

	useEffect(() => {
		if (isFetching || isError || !data?.items.length || !filtersActive) {
			return;
		}

		setSelectedGroups((prev) => {
			const next = new Map(prev);
			let changed = false;

			for (const group of data.items) {
				if (!next.has(group.id)) {
					changed = true;
				}
				next.set(group.id, group);
			}

			if (changed) {
				onSelectionChange(Array.from(next.values()));
			}

			return next;
		});
	}, [data, filtersActive, isError, isFetching]);

	const handleToggleSelect = (group: CzechSuklRsAtcGroupResponse, checked: boolean) => {
		setSelectedGroups((prev) => {
			const next = new Map(prev);
			if (checked) {
				next.set(group.id, group);
			} else {
				next.delete(group.id);
			}
			onSelectionChange(Array.from(next.values()));
			return next;
		});
	};

	return (
		<div className='flex flex-col h-full min-h-0'>
			<div className='flex-shrink-0'>
				<Filters value={filters} onChange={handleFiltersChange} />

				<p className='text-sm text-gray-500 -mt-2 mb-2'>
					{selectedGroups.size > 0
						? filtersActive
							? `Vybráno skupin: ${selectedGroups.size} (automaticky dle filtru)`
							: `Vybráno skupin: ${selectedGroups.size}`
						: filtersActive
						? 'Načítání výsledků filtru…'
						: 'Kliknutím na řádek jej vyberte do skupiny.'}
				</p>
			</div>

			<div className='flex-1 min-h-0 overflow-auto space-y-4 pb-2'>
				{isFetching && (
					<div className='flex justify-center py-10'>
						<SpinningLoadingIcon />
					</div>
				)}

				{!isFetching && isError && (
					<div className='py-10 text-center text-red-600'>Data se nepodařilo načíst. Zkuste to prosím znovu.</div>
				)}

				{!isFetching && !isError && data?.items.length === 0 && (
					<div className='py-10 text-center text-gray-500'>Nic nenalezeno.</div>
				)}

				{!isFetching &&
					!isError &&
					data?.items.map((group) => (
						<Row
							key={group.id}
							group={group}
							dddShareThreshold={DDD_SHARE_THRESHOLD}
							selectable
							hideCheckbox
							selected={selectedGroups.has(group.id)}
							onToggleSelect={handleToggleSelect}
						/>
					))}
			</div>

			{!isFetching && !isError && data && data.items.length > 0 && (
				<div className='flex-shrink-0 pt-3 border-t border-gray-200'>
					<PaginationPanel pagination={data.pagination} onChange={setPage} />
				</div>
			)}
		</div>
	);
}
